import { defineNuxtPlugin } from '#app'

/**
 * Splits merged tables into separate tables for WCAG 1.3.1 compliance.
 * Detects rows that act as "section headers" (single cell with colspan spanning all columns)
 * and splits the table at those points.
 */
function splitMergedTables(html: string): string {
    if (!html || !html.includes('<table')) return html

    // Only run on client side where DOMParser is available
    if (typeof window === 'undefined') return html

    const parser = new DOMParser()
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html')
    const container = doc.body.firstElementChild as HTMLElement

    if (!container) return html

    const tables = container.querySelectorAll('table')

    tables.forEach((table) => {
        const rows = Array.from(table.querySelectorAll('tr'))
        if (rows.length < 3) return // Too small to split

        // Find the number of columns in the table
        let maxCols = 0
        rows.forEach((row) => {
            let colCount = 0
            row.querySelectorAll('th, td').forEach((cell) => {
                colCount += parseInt(cell.getAttribute('colspan') || '1', 10)
            })
            if (colCount > maxCols) maxCols = colCount
        })

        if (maxCols < 2) return // Single column table, skip

        // Find rows that look like section headers (single cell spanning all columns)
        const splitPoints: number[] = []
        rows.forEach((row, index) => {
            // Skip first row (likely actual header)
            if (index === 0) return

            const cells = row.querySelectorAll('th, td')
            if (cells.length === 1) {
                const cell = cells[0]
                const colspan = parseInt(cell?.getAttribute('colspan') || '1', 10)
                // Check if this cell spans all or most columns and contains text
                if (colspan >= maxCols - 1 && cell?.textContent?.trim()) {
                    splitPoints.push(index)
                }
            }
        })

        // If no split points found, nothing to do
        if (splitPoints.length === 0) return

        // Create new tables
        const fragment = document.createDocumentFragment()
        let currentStart = 0

        splitPoints.forEach((splitIndex) => {
            // Get header text from the split row
            const headerRow = rows[splitIndex]!
            const headerCell = headerRow.querySelector('th, td')
            const headerText = headerCell?.textContent?.trim() || ''

            // Create table for rows before this split point (if any data rows exist)
            if (splitIndex > currentStart) {
                const newTable = createTableFromRows(table, rows, currentStart, splitIndex)
                if (newTable) {
                    // Wrap in scrollable div
                    const wrapper = document.createElement('div')
                    wrapper.className = 'w-100 overflow-auto'
                    wrapper.appendChild(newTable)
                    fragment.appendChild(wrapper)
                }
            }

            // Add heading for next section
            if (headerText) {
                const heading = document.createElement('h3')
                heading.className = 'mt-6 mb-4'
                heading.textContent = headerText
                fragment.appendChild(heading)
            }

            currentStart = splitIndex + 1
        })

        // Create table for remaining rows
        if (currentStart < rows.length) {
            const newTable = createTableFromRows(table, rows, currentStart, rows.length)
            if (newTable) {
                const wrapper = document.createElement('div')
                wrapper.className = 'w-100 overflow-auto'
                wrapper.appendChild(newTable)
                fragment.appendChild(wrapper)
            }
        }

        // Replace original table with fragment
        table.parentNode?.replaceChild(fragment, table)
    })

    return container.innerHTML
}

/**
 * Creates a new table from a subset of rows
 */
function createTableFromRows(
    originalTable: HTMLTableElement,
    rows: HTMLTableRowElement[],
    startIndex: number,
    endIndex: number
): HTMLTableElement | null {
    const dataRows = rows.slice(startIndex, endIndex)
    if (dataRows.length === 0) return null

    const newTable = document.createElement('table')

    // Copy table attributes
    Array.from(originalTable.attributes).forEach((attr) => {
        newTable.setAttribute(attr.name, attr.value)
    })

    // If first section (startIndex === 0), include the header row
    // Otherwise, we need to copy the original header
    if (startIndex === 0) {
        // First section includes original header
        const tbody = document.createElement('tbody')
        dataRows.forEach((row) => {
            tbody.appendChild(row.cloneNode(true))
        })
        newTable.appendChild(tbody)
    } else {
        // Other sections: copy header from original table and add data rows
        const originalHeader = rows[0]
        if (originalHeader) {
            const thead = document.createElement('thead')
            thead.appendChild(originalHeader.cloneNode(true))
            newTable.appendChild(thead)
        }

        const tbody = document.createElement('tbody')
        dataRows.forEach((row) => {
            tbody.appendChild(row.cloneNode(true))
        })
        newTable.appendChild(tbody)
    }

    return newTable
}

/**
 * Marks layout tables (tables without headers) with role="presentation"
 * so screen readers don't announce row/column information (WCAG 1.3.1)
 */
function markLayoutTables(html: string): string {
    if (!html || !html.includes('<table')) return html

    // Only run on client side where DOMParser is available
    if (typeof window === 'undefined') return html

    const parser = new DOMParser()
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html')
    const container = doc.body.firstElementChild as HTMLElement

    if (!container) return html

    const tables = container.querySelectorAll('table')
    let modified = false

    tables.forEach((table) => {
        // Skip tables that already have role attribute
        if (table.hasAttribute('role')) return

        // Check if table has any <th> elements
        const hasHeaders = table.querySelectorAll('th').length > 0

        // Check if table has proper structure (thead/tbody)
        const hasThead = table.querySelector('thead') !== null

        // If no headers and no thead, it's likely a layout table
        if (!hasHeaders && !hasThead) {
            table.setAttribute('role', 'presentation')
            modified = true
        }
    })

    return modified ? container.innerHTML : html
}

/**
 * Wraps tables in scrollable containers
 */
function wrapTablesInScrollContainer(html: string): string {
    if (!html?.replaceAll) return html
    return html
        .replaceAll('<table', '<div class="w-100 overflow-auto"><table')
        .replaceAll('</table>', '</table></div>')
}

/**
 * Enhances footnotes for accessibility (WCAG 1.3.1, 2.4.4)
 * Converts numbered superscripts to proper footnote references with navigation
 */
function enhanceFootnotes(html: string): string {
    if (!html || typeof window === 'undefined') return html

    // Check if there are potential footnotes (superscripts with numbers or brackets)
    if (!/<sup[^>]*>.*?<\/sup>/i.test(html)) return html

    const parser = new DOMParser()
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html')
    const container = doc.body.firstElementChild as HTMLElement

    if (!container) return html

    // Generate unique ID prefix for this content block
    const idPrefix = `fn-${Math.random().toString(36).substring(2, 11)}`

    // First, find footnote definitions (paragraphs starting with <sup>number</sup>)
    // These are typically after <hr> and define what footnotes mean
    const footnoteDefinitions: { element: Element; number: string }[] = []
    const paragraphs = container.querySelectorAll('p')

    paragraphs.forEach((p) => {
        const firstChild = p.firstElementChild
        // Check if paragraph starts with <sup> containing a number
        if (firstChild?.tagName === 'SUP') {
            const supText = firstChild.textContent?.trim() || ''
            const match = supText.match(/^(\d+)\s*$/)
            if (match) {
                footnoteDefinitions.push({ element: p, number: match[1]! })
            }
        }
    })

    // Find all superscripts that look like footnote references (in the main text)
    const sups = container.querySelectorAll('sup')
    const footnoteRefs: { element: Element; number: string }[] = []

    sups.forEach((sup) => {
        // Get text content, handling nested elements like <em>
        const text = sup.textContent?.trim() || ''
        // Match patterns like "1", "[1]", "(1)", "*", "†"
        const match = text.match(/^\[?(\d+)\]?$|^(\*+|†+|‡+)$/)
        if (match) {
            const number = match[1] || match[2] || text
            // Check if this is a reference (in text) not a definition (at bottom)
            const parent = sup.parentElement
            // Skip if this sup is at the start of a paragraph (it's a definition)
            if (parent?.tagName === 'P' && parent.firstElementChild === sup) {
                return
            }
            footnoteRefs.push({ element: sup, number })
        }
    })

    if (footnoteRefs.length === 0) return html

    // Enhance footnote references in text
    footnoteRefs.forEach(({ element: sup, number }, index) => {
        const refId = `${idPrefix}-ref-${index + 1}`
        const noteId = `${idPrefix}-note-${index + 1}`

        // Check if already wrapped in a link
        const parent = sup.parentElement
        if (parent?.tagName === 'A') {
            // Enhance existing link
            parent.setAttribute('id', refId)
            parent.setAttribute('role', 'doc-noteref')
            parent.setAttribute('aria-label', `Poznámka ${number}`)
            if (!parent.getAttribute('href')?.startsWith('#')) {
                parent.setAttribute('href', `#${noteId}`)
            }
        } else {
            // Wrap in a new link
            const link = document.createElement('a')
            link.setAttribute('id', refId)
            link.setAttribute('href', `#${noteId}`)
            link.setAttribute('role', 'doc-noteref')
            link.setAttribute('aria-label', `Poznámka ${number}`)
            link.className = 'footnote-ref'

            sup.parentNode?.insertBefore(link, sup)
            link.appendChild(sup)
        }
    })

    // Find and enhance footnote definitions (usually in lists at the end)
    // Look for patterns like ordered lists or paragraphs starting with numbers
    const lists = container.querySelectorAll('ol, ul')
    lists.forEach((list) => {
        const items = list.querySelectorAll('li')
        let isFootnoteList = false

        items.forEach((item, index) => {
            const text = item.textContent?.trim() || ''
            // Check if list item starts with a footnote number pattern
            if (/^[[(]?\d+[\])]?[.:\s]/.test(text) || index < footnoteRefs.length) {
                isFootnoteList = true
            }
        })

        if (isFootnoteList && items.length > 0) {
            list.setAttribute('role', 'doc-endnotes')

            items.forEach((item, index) => {
                if (index < footnoteRefs.length) {
                    const noteId = `${idPrefix}-note-${index + 1}`
                    const refId = `${idPrefix}-ref-${index + 1}`

                    item.setAttribute('id', noteId)
                    item.setAttribute('role', 'doc-endnote')

                    // Add back link if not already present
                    if (!item.querySelector('a[href^="#' + idPrefix + '-ref"]')) {
                        const backLink = document.createElement('a')
                        backLink.setAttribute('href', `#${refId}`)
                        backLink.setAttribute('aria-label', 'Späť na text')
                        backLink.className = 'footnote-back'
                        backLink.textContent = ' ↩'
                        item.appendChild(backLink)
                    }
                }
            })
        }
    })

    // Also look for footnote definitions in paragraphs
    // CMS pattern: <p><sup>1 </sup>text...</p> or "1) text..." or "1. text..."
    const allParagraphs = container.querySelectorAll('p')
    const footnoteParas: { element: Element; number: number }[] = []

    allParagraphs.forEach((p) => {
        // Check for CMS pattern: paragraph starting with <sup>number</sup>
        const firstChild = p.firstElementChild
        if (firstChild?.tagName === 'SUP') {
            const supText = firstChild.textContent?.trim() || ''
            const supMatch = supText.match(/^(\d+)\s*$/)
            if (supMatch) {
                const num = parseInt(supMatch[1]!, 10)
                if (num > 0 && num <= footnoteRefs.length) {
                    footnoteParas.push({ element: p, number: num })
                    return
                }
            }
        }

        // Also check for text pattern: "1)", "1.", "[1]", "(1)" at the start
        const text = p.textContent?.trim() || ''
        const match = text.match(/^[[(]?(\d+)[\]).][:\s]/)
        if (match && match[1]) {
            const num = parseInt(match[1], 10)
            if (num > 0 && num <= footnoteRefs.length) {
                footnoteParas.push({ element: p, number: num })
            }
        }
    })

    // If we found paragraph-based footnotes, enhance them
    if (footnoteParas.length > 0) {
        // Wrap consecutive footnote paragraphs in a section
        const firstFootnote = footnoteParas[0]!.element
        const parent = firstFootnote.parentElement

        // Check if there's an <hr> before footnotes (common CMS pattern)
        let hrElement: Element | null = null
        let prevSibling = firstFootnote.previousElementSibling
        while (prevSibling) {
            if (prevSibling.tagName === 'HR') {
                hrElement = prevSibling
                break
            }
            prevSibling = prevSibling.previousElementSibling
        }

        // Create footnotes section wrapper
        const footnotesSection = document.createElement('section')
        footnotesSection.setAttribute('role', 'doc-endnotes')
        footnotesSection.setAttribute('aria-label', 'Poznámky pod čiarou')
        footnotesSection.className = 'footnotes-section'

        // Insert section before first footnote
        parent?.insertBefore(footnotesSection, firstFootnote)

        // Move footnote paragraphs into the section
        footnoteParas.forEach(({ element: p, number }) => {
            const noteId = `${idPrefix}-note-${number}`
            const refId = `${idPrefix}-ref-${number}`

            p.setAttribute('id', noteId)
            p.setAttribute('role', 'doc-endnote')
            p.classList.add('footnote-item')

            // Add back link if not already present
            if (!p.querySelector('.footnote-back')) {
                const backLink = document.createElement('a')
                backLink.setAttribute('href', `#${refId}`)
                backLink.setAttribute('aria-label', 'Späť na text')
                backLink.className = 'footnote-back'
                backLink.textContent = ' ↩'
                p.appendChild(backLink)
            }

            footnotesSection.appendChild(p)
        })

        // Remove <hr> if it was just a visual separator before footnotes
        if (hrElement) {
            hrElement.remove()
        }
    }

    return container.innerHTML
}

/**
 * Ensures all images have alt attributes for accessibility (WCAG 1.1.1)
 * - Images without alt get alt="" (marked as decorative)
 * - This prevents screen readers from reading the filename
 */
function ensureImageAltAttributes(html: string): string {
    if (!html || !html.includes('<img')) return html

    // Use regex for both SSR and client-side to ensure alt is always present
    // Match <img tags that don't have an alt attribute
    // This regex finds <img ... > without alt= anywhere in the tag
    let modified = false
    const result = html.replace(/<img\s+(?![^>]*\balt\s*=)([^>]*)>/gi, (_match, attrs) => {
        modified = true
        // Add alt="" before the closing >
        return `<img ${attrs} alt="">`
    })

    // Also ensure existing alt="" images have aria-hidden for true decoratives
    // (Images with alt="" should be hidden from screen readers)

    return modified ? result : html
}

function makeImagesNoAccessible(html: string): string {
    return html.replace(/<img\b([^>]*)>/gi, (_match, attrs) => {
        const withoutAriaHidden = attrs.replace(/\s+aria-hidden\s*=\s*("[^"]*"|'[^']*'|\S+)/gi, '')
        return `<img${withoutAriaHidden} aria-hidden="true">`
    })
}

export default defineNuxtPlugin(() => {
    return {
        provide: {
            formatTableHtml: (html: string): string => {
                if (!html) return html

                // First try to split merged tables (client-side only)
                let result = splitMergedTables(html)

                // Mark layout tables (tables without headers) with role="presentation"
                result = markLayoutTables(result)

                // Enhance footnotes for accessibility (WCAG 1.3.1, 2.4.4)
                result = enhanceFootnotes(result)

                // Ensure all images have alt attributes (WCAG 1.1.1)
                result = ensureImageAltAttributes(result)

                // Make images no accessible
                result = makeImagesNoAccessible(result)

                // If we're on server or no modifications were made,
                // just wrap tables in scroll containers
                result = wrapTablesInScrollContainer(result)

                return result
            },
        },
    }
})
